/**
 * CodesPage — the referral analytics cards + CodeReferral creation. Two tabs:
 *   • Analytics — KPI tiles (médecins / approuvés / invites en attente / retours attribués), a Recharts
 *     "retours par médecin" distribution, and a per-médecin table whose "remise active" column lazily
 *     resolves GET /referral/candidate (the live discount candidate, or "Aucune").
 *   • Gestion des codes — the approuve-link médecins, each with a "Créer un code" action (the code form
 *     pins that médecin). CodeReferral toggle (actif↔inactif) is per-code; codes are created here and the
 *     server has no list-codes read, so the management tab focuses on creation per eligible médecin.
 *
 * Data: GET /referral/analytics (roster) + per-row GET /referral/candidate. Full 5-state DoD + the
 * CLOUD-only "indisponible" state via QueryBoundary. Mutations gated on referral.code.manage; the
 * candidate column needs referral.remise.set.
 */
import { useMemo, useState } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge, Button, Card, Table, Tabs, type Column } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { REF_CAPS } from "../caps";
import { useAnalytics, useCandidate, useLabId } from "../api";
import type { MedecinCard } from "../types";
import { fmtPct, isLinkLive } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { LienBadge } from "../components/StatusBadge";
import { QueryBoundary } from "../components/QueryBoundary";
import { CodeFormModal } from "../components/CodeFormModal";

export function CodesPage() {
  const { t } = useT();
  const labId = useLabId();
  const canRead = useCan(REF_CAPS.analyticsRead);
  const canManage = useCan(REF_CAPS.codeManage);
  const canRemise = useCan(REF_CAPS.remiseSet);

  const query = useAnalytics(labId, canRead);
  const cards = useMemo(() => query.data?.cards ?? [], [query.data]);

  const [tab, setTab] = useState("analytics");
  const [codeFor, setCodeFor] = useState<string | null | undefined>(undefined); // undefined = closed

  const kpis = useMemo(() => {
    let approuves = 0;
    let invites = 0;
    let retours = 0;
    for (const c of cards) {
      if (c.lien_statut === "approuve") approuves += 1;
      if (c.lien_statut === "invite") invites += 1;
      retours += c.retours_count;
    }
    return { medecins: cards.length, approuves, invites, retours };
  }, [cards]);

  const chartData = useMemo(
    () =>
      [...cards]
        .filter((c) => c.retours_count > 0)
        .sort((a, b) => b.retours_count - a.retours_count)
        .slice(0, 8)
        .map((c) => ({ id: c.medecin_id, label: c.nom, value: c.retours_count })),
    [cards],
  );

  const eligible = useMemo(() => cards.filter((c) => isLinkLive(c.lien_statut)), [cards]);

  const analyticsColumns: ReadonlyArray<Column<MedecinCard>> = [
    {
      key: "nom",
      header: t("referral.analytics.col.nom"),
      sortable: true,
      sortAccessor: (r) => r.nom,
      cell: (r) => r.nom,
    },
    {
      key: "email",
      header: t("referral.analytics.col.email"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.novera_email}</span>,
    },
    {
      key: "statut",
      header: t("referral.analytics.col.statut"),
      cell: (r) => <LienBadge statut={r.lien_statut} />,
    },
    {
      key: "retours",
      header: t("referral.analytics.col.retours"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.retours_count,
      cell: (r) => <span className="nv-ltr-island">{r.retours_count}</span>,
    },
    {
      key: "candidate",
      header: t("referral.analytics.col.candidate"),
      align: "end",
      cell: (r) => <CandidateCell labId={labId} medecinId={r.medecin_id} enabled={canRemise} />,
    },
  ];

  const manageColumns: ReadonlyArray<Column<MedecinCard>> = [
    {
      key: "nom",
      header: t("referral.analytics.col.nom"),
      sortable: true,
      sortAccessor: (r) => r.nom,
      cell: (r) => r.nom,
    },
    {
      key: "email",
      header: t("referral.analytics.col.email"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.novera_email}</span>,
    },
    {
      key: "statut",
      header: t("referral.analytics.col.statut"),
      cell: (r) => <LienBadge statut={r.lien_statut} />,
    },
    {
      key: "actions",
      header: t("referral.analytics.col.actions"),
      align: "end",
      cell: (r) => (
        <Button
          size="sm"
          variant="secondary"
          disabled={!canManage}
          onClick={() => setCodeFor(r.medecin_id)}
        >
          {t("referral.analytics.action.code")}
        </Button>
      ),
    },
  ];

  const header = (
    <PageHeader
      title={t("referral.codes.title")}
      subtitle={t("referral.codes.subtitle")}
      actions={
        <Button disabled={!canManage || eligible.length === 0} onClick={() => setCodeFor(null)}>
          {t("referral.codes.new")}
        </Button>
      }
    />
  );

  const analyticsPanel = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "var(--nv-s-3)",
        }}
      >
        <Kpi label={t("referral.analytics.kpi.medecins")} value={kpis.medecins} />
        <Kpi
          label={t("referral.analytics.kpi.approuves")}
          value={kpis.approuves}
          tokenColor="var(--nv-success)"
        />
        <Kpi
          label={t("referral.analytics.kpi.invites")}
          value={kpis.invites}
          tokenColor="var(--nv-info)"
        />
        <Kpi
          label={t("referral.analytics.kpi.retours")}
          value={kpis.retours}
          tokenColor="var(--nv-azure-600)"
        />
      </div>

      <Card title={t("referral.analytics.chart.title")}>
        {chartData.length === 0 ? (
          <p style={{ color: "var(--nv-slate)", margin: 0 }}>
            {t("referral.analytics.chart.empty")}
          </p>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <XAxis dataKey="label" stroke="var(--nv-slate)" fontSize={12} tickLine={false} />
                <YAxis allowDecimals={false} stroke="var(--nv-slate)" fontSize={12} width={28} />
                <Tooltip
                  cursor={{ fill: "var(--nv-surface-2)" }}
                  contentStyle={{
                    background: "var(--nv-surface)",
                    border: "1px solid var(--nv-line)",
                    borderRadius: "var(--nv-r-sm)",
                    color: "var(--nv-ink)",
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {chartData.map((d) => (
                    <Cell key={d.id} fill="var(--nv-azure)" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={analyticsColumns}
            rows={cards}
            rowKey={(r) => r.medecin_id}
            caption={t("referral.analytics.chart.title")}
            pageSize={12}
            emptyTitle={t("referral.codes.empty.title")}
            emptyDescription={t("referral.codes.empty.desc")}
          />
        </div>
      </Card>
    </div>
  );

  const managePanel = (
    <Card padded={false}>
      <div style={{ padding: "var(--nv-s-3)" }}>
        <Table
          columns={manageColumns}
          rows={eligible}
          rowKey={(r) => r.medecin_id}
          caption={t("referral.codes.tab.manage")}
          pageSize={12}
          emptyTitle={t("referral.codes.empty.title")}
          emptyDescription={t("referral.codes.empty.desc")}
        />
      </div>
    </Card>
  );

  return (
    <>
      <QueryBoundary
        forbidden={!canRead}
        forbiddenCapabilities={[REF_CAPS.analyticsRead]}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error ?? null}
        hasData={cards.length > 0}
        onRetry={() => void query.refetch()}
        header={header}
      >
        {header}
        <Tabs
          value={tab}
          onChange={setTab}
          aria-label={t("referral.codes.title")}
          items={[
            { id: "analytics", label: t("referral.codes.tab.analytics"), content: analyticsPanel },
            { id: "manage", label: t("referral.codes.tab.manage"), content: managePanel },
          ]}
        />
      </QueryBoundary>

      <CodeFormModal
        open={codeFor !== undefined}
        labId={labId}
        cards={cards}
        lockedMedecinId={codeFor ?? null}
        disabled={!canManage}
        onClose={() => setCodeFor(undefined)}
        onCreated={() => void query.refetch()}
      />
    </>
  );
}

/** A lazily-resolved "remise active" cell — runs GET /referral/candidate per médecin (cached). */
function CandidateCell({
  labId,
  medecinId,
  enabled,
}: {
  labId: string | null;
  medecinId: string;
  enabled: boolean;
}) {
  const { t } = useT();
  const q = useCandidate(labId, medecinId, enabled);
  if (!enabled) return <span style={{ color: "var(--nv-steel)" }}>—</span>;
  if (q.isLoading) return <span style={{ color: "var(--nv-steel)" }}>{t("referral.analytics.candidate.loading")}</span>;
  const candidate = q.data?.candidate ?? null;
  if (!candidate) {
    return (
      <Badge tone="neutral" glyph="○">
        {t("referral.analytics.candidate.none")}
      </Badge>
    );
  }
  return (
    <Badge tone="success" glyph="●">
      <span className="nv-ltr-island">{fmtPct(candidate.pct)}</span>
    </Badge>
  );
}

function Kpi({
  label,
  value,
  tokenColor,
}: {
  label: string;
  value: number;
  tokenColor?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-3)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>{label}</span>
      <span
        className="nv-ltr-island"
        style={{ fontSize: "var(--nv-fs-h2)", fontWeight: 600, color: tokenColor ?? "var(--nv-ink)" }}
      >
        {value}
      </span>
    </div>
  );
}
